import type { Id } from "@repo/convex/dataModel";
import { buildAssistantThreadPath, buildProjectSnapshotPath } from "@repo/shared";
import { ArrowLeftRight, Camera, History, Pencil, Trash2, TriangleAlert } from "lucide-react";
import type { KeyboardEvent } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingState } from "@/components/ui/loading-state";
import { ScrollShadow } from "@/components/ui/scroll-shadow";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTabsActions } from "@/contexts/tabs-context";
import { resolveUser, useCurrentUser, useOrgUserMap } from "@/hooks";
import { trackVersionEvent } from "@/lib/analytics/domain";
import { formatSmartDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import {
  useRightSidebarEntity,
  useRightSidebarProject,
  type VersionCompareRouteBehavior,
} from "../../contexts";
import { useRecordEntityTouch } from "../../hooks";
import {
  useProjectFrameworkStructure,
  useVersionsByEntity,
  useVersionThreadProvenance,
} from "../../hooks/useProjectData";
import {
  buildEntityDisplayMap,
  catalogHasEntityType,
  getCatalogEntityTypeDisplay,
  type ProjectFrameworkCatalog,
} from "../../lib/framework-registry";
import { VersionOriginBadge, type VersionThreadProvenanceView } from "./version-origin-badge";

interface VersionItem {
  id: Id<"entityVersions">;
  version: string;
  summary: string;
  author: string;
  timestamp: Date;
  isCurrent: boolean;
  isDraft: boolean;
  isOutdatedDraft: boolean;
  /**
   * Who made the change. `"ai"` is the only value that changes what the row renders — it earns
   * a Claudia mark next to the author; every other origin looks exactly as it always has.
   */
  originKind: string | null;
  /** The Claudia conversation the change came from, when one was recorded. */
  originAssistantThreadId: string | null;
  /** Display version number (e.g., "3" for drafts showing next increment) */
  displayVersion: string;
  projectSnapshots: Array<{
    id: Id<"projectSnapshots">;
    name: string;
    releaseNumber: number;
    kind: "manual" | "ai-baseline" | "ai-checkpoint" | "restored";
  }>;
}

interface ResolveVersionRowStateOptions {
  version: Pick<VersionItem, "isCurrent" | "isDraft" | "isOutdatedDraft">;
  isViewedVersion: boolean;
  isCompareTarget: boolean;
  isPreviewRow: boolean;
  isDiffMode: boolean;
}

type CompareNavigableVersion = Pick<
  VersionItem,
  "id" | "isCurrent" | "isDraft" | "isOutdatedDraft"
>;

type VersionSelectionRouteAction = "base" | "preview";

interface ResolveCompareNavigationTargetOptions {
  versionHistory: CompareNavigableVersion[];
  diffTargetVersionId: Id<"entityVersions"> | null;
  rowIndex: number;
  step: 1 | -1;
  isVersionViewed: (version: CompareNavigableVersion) => boolean;
}

interface ResolveComparePairShiftOptions {
  viewedIndex: number;
  compareIndex: number;
  step: 1 | -1;
  versionCount: number;
}

const NON_TRACKABLE_CATALOG_ENTITY_TYPE_KEYS = new Set(["folder"]);

function toTrackedEntityTypeKey(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityType: string | null
): string | null {
  if (!entityType) return null;
  if (NON_TRACKABLE_CATALOG_ENTITY_TYPE_KEYS.has(entityType)) return null;
  return catalogHasEntityType(catalog, entityType) ? entityType : null;
}

function formatSnapshotLabel(snapshot: { releaseNumber: number; name: string }) {
  return `Snapshot #${snapshot.releaseNumber}: ${snapshot.name}`;
}

/**
 * The distinct Claudia conversations behind the rows on screen.
 *
 * One query per panel open, not one per version: a history where the assistant made twenty
 * changes in the same chat asks about that chat once.
 */
export function collectAssistantThreadIds(
  versions: ReadonlyArray<Pick<VersionItem, "originKind" | "originAssistantThreadId">>
): string[] {
  const seen = new Set<string>();
  for (const version of versions) {
    if (version.originKind !== "ai") continue;
    if (!version.originAssistantThreadId) continue;
    seen.add(version.originAssistantThreadId);
  }
  return [...seen];
}

// ── Version row color system ──────────────────────────────────────────
// Each state maps to a consistent set of colors for pill, label, row bg,
// summary border, and summary text.

type VersionRowState =
  | "normal"
  | "current"
  | "currentCompare"
  | "preview"
  | "compareTarget"
  | "draft"
  | "outdatedDraft";

interface VersionRowColors {
  pillBg: string;
  pillText: string;
  label: string | null;
  labelColor: string;
  rowBg: string;
  summaryBorder: string;
  summaryText: string;
}

const VERSION_ROW_COLORS: Record<VersionRowState, VersionRowColors> = {
  normal: {
    pillBg: "bg-neutral-muted",
    pillText: "text-neutral-muted-fg",
    label: null,
    labelColor: "",
    rowBg: "",
    summaryBorder: "border-neutral-border",
    summaryText: "text-neutral-dim",
  },
  current: {
    pillBg: "bg-blue-600",
    pillText: "text-white",
    label: "Current",
    labelColor: "text-blue-600",
    rowBg: "bg-surface-blue",
    summaryBorder: "border-neutral-border",
    summaryText: "text-neutral-dim",
  },
  currentCompare: {
    pillBg: "bg-blue-600",
    pillText: "text-white",
    label: "Compare",
    labelColor: "text-blue-600",
    rowBg: "bg-surface-blue",
    summaryBorder: "border-blue-border",
    summaryText: "text-blue-content",
  },
  preview: {
    pillBg: "bg-orange-600",
    pillText: "text-white",
    label: null, // set dynamically: "Preview" or "Compare"
    labelColor: "text-orange-600",
    rowBg: "bg-surface-orange",
    summaryBorder: "border-orange-border",
    summaryText: "text-orange-content",
  },
  compareTarget: {
    pillBg: "bg-green-600",
    pillText: "text-white",
    label: "Compare",
    labelColor: "text-green-600",
    rowBg: "bg-surface-green",
    summaryBorder: "border-green-border",
    summaryText: "text-green-content",
  },
  draft: {
    pillBg: "bg-amber-600",
    pillText: "text-white",
    label: "Editing",
    labelColor: "text-amber-600",
    rowBg: "bg-surface-amber",
    summaryBorder: "",
    summaryText: "",
  },
  outdatedDraft: {
    pillBg: "bg-red-600",
    pillText: "text-white",
    label: "Outdated",
    labelColor: "text-red-600",
    rowBg: "bg-surface-red",
    summaryBorder: "",
    summaryText: "",
  },
};

export function getVersionSummaryText(version: Pick<VersionItem, "version" | "summary">): string {
  if (version.version === "1" && !version.summary) return "Initial version";
  return version.summary || "No change description";
}

export function resolveVersionRowState({
  version,
  isViewedVersion,
  isCompareTarget,
  isPreviewRow,
  isDiffMode,
}: ResolveVersionRowStateOptions): VersionRowState {
  if (isDiffMode && (isViewedVersion || isCompareTarget)) {
    return "compareTarget";
  }

  if (version.isOutdatedDraft) return "outdatedDraft";
  if (version.isDraft) return "draft";

  if (isPreviewRow) return "preview";
  if (version.isCurrent) return "current";

  return "normal";
}

export function resolveCompareNavigationTarget({
  versionHistory,
  diffTargetVersionId,
  rowIndex,
  step,
  isVersionViewed,
}: ResolveCompareNavigationTargetOptions): number | null {
  const compareIndex = versionHistory.findIndex((version) => version.id === diffTargetVersionId);
  let candidateIndex = (compareIndex === -1 ? rowIndex : compareIndex) + step;

  while (candidateIndex >= 0 && candidateIndex < versionHistory.length) {
    const candidateVersion = versionHistory[candidateIndex];
    if (!candidateVersion) {
      return null;
    }

    if (!isVersionViewed(candidateVersion)) {
      return candidateIndex;
    }

    candidateIndex += step;
  }

  return null;
}

export function resolveComparePairShift({
  viewedIndex,
  compareIndex,
  step,
  versionCount,
}: ResolveComparePairShiftOptions): { baselineIndex: number; targetIndex: number } | null {
  if (viewedIndex < 0 || compareIndex < 0 || Math.abs(viewedIndex - compareIndex) !== 1) {
    return null;
  }

  const baselineIndex = viewedIndex + step;
  const targetIndex = compareIndex + step;

  if (
    baselineIndex < 0 ||
    targetIndex < 0 ||
    baselineIndex >= versionCount ||
    targetIndex >= versionCount
  ) {
    return null;
  }

  return { baselineIndex, targetIndex };
}

export function isVersionViewedForRoute({
  version,
  currentViewedVersionId,
  hasAnyDraft,
}: {
  version: Pick<VersionItem, "id" | "isCurrent" | "isDraft">;
  currentViewedVersionId: Id<"entityVersions"> | null;
  hasAnyDraft: boolean;
}): boolean {
  if (currentViewedVersionId) {
    return version.id === currentViewedVersionId;
  }

  return hasAnyDraft ? version.isDraft : version.isCurrent;
}

export function getVersionSelectionRouteAction(
  version: Pick<VersionItem, "isCurrent" | "isDraft">,
  hasAnyDraft: boolean
): VersionSelectionRouteAction {
  if (version.isDraft) {
    return "base";
  }

  if (version.isCurrent && !hasAnyDraft) {
    return "base";
  }

  return "preview";
}

export function resolveBaselineVersionId(
  version: Pick<VersionItem, "id" | "isCurrent" | "isDraft">,
  hasAnyDraft: boolean
): Id<"entityVersions"> | null {
  if (version.isDraft) {
    return null;
  }

  if (version.isCurrent && !hasAnyDraft) {
    return null;
  }

  return version.id;
}

function getSavedVersionNumber(version: VersionItem): number | undefined {
  if (version.isDraft) {
    return undefined;
  }

  const parsed = Number(version.version);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
}

export function getCompareTooltipText(
  viewedVersion: Pick<VersionItem, "displayVersion" | "isCurrent" | "isDraft"> | null
): string {
  if (viewedVersion && !viewedVersion.isCurrent && !viewedVersion.isDraft) {
    return `Compare with v${viewedVersion.displayVersion}`;
  }

  return "Compare with current";
}

export function getCompareRouteBehavior({
  targetVersion,
  hasAnyDraft,
  currentViewedVersionId,
}: {
  targetVersion: Pick<VersionItem, "isDraft">;
  hasAnyDraft: boolean;
  currentViewedVersionId: Id<"entityVersions"> | null;
}): VersionCompareRouteBehavior {
  if (targetVersion.isDraft || (hasAnyDraft && currentViewedVersionId === null)) {
    return "preserve";
  }

  return "saved-diff";
}

export function getComparePairRouteBehavior(
  baselineVersion: Pick<VersionItem, "isDraft">,
  targetVersion: Pick<VersionItem, "isDraft">
): VersionCompareRouteBehavior {
  return baselineVersion.isDraft || targetVersion.isDraft ? "preserve" : "saved-diff";
}

export function VersionsPanel() {
  const { projectId, tabId } = useRightSidebarProject();
  const { navigateTab } = useTabsActions();
  const {
    entityType,
    entityId,
    hasVersionController,
    versionsViewState,
    requestVersionPreview,
    requestExitVersionPreview,
    requestVersionCompare,
    requestVersionComparePair,
    requestDiscardDraft,
  } = useRightSidebarEntity();
  const { currentViewedVersionId, diffTargetVersionId, isDiffMode } = versionsViewState;
  const currentUser = useCurrentUser();
  const userMap = useOrgUserMap();
  const frameworkStructure = useProjectFrameworkStructure(projectId);
  const versionsData = useVersionsByEntity(
    projectId,
    entityType,
    entityId,
    currentUser?._id ?? null
  );
  const recordEntityTouch = useRecordEntityTouch();
  const trackedPanelOpenKeyRef = useRef<string | null>(null);
  const entityDisplays = useMemo(
    () => buildEntityDisplayMap(frameworkStructure ?? null),
    [frameworkStructure]
  );

  const handleDeleteDraft = useCallback(() => {
    requestExitVersionPreview();
    requestDiscardDraft();
  }, [requestExitVersionPreview, requestDiscardDraft]);

  const handleOpenProjectSnapshots = useCallback(() => {
    navigateTab(tabId, buildProjectSnapshotPath(), {
      rc: undefined,
    });
  }, [navigateTab, tabId]);

  const trackedEntityTypeKey = useMemo(
    () => toTrackedEntityTypeKey(frameworkStructure?.compiledCatalog, entityType),
    [entityType, frameworkStructure?.compiledCatalog]
  );

  const trackTouch = useCallback(
    (reason: "version_preview" | "version_compare") => {
      if (!trackedEntityTypeKey || !entityId) {
        return;
      }

      void recordEntityTouch({
        projectId,
        entityTypeKey: trackedEntityTypeKey,
        entityId,
        reason,
      }).catch(() => {
        // Best-effort tracking only.
      });
    },
    [entityId, projectId, recordEntityTouch, trackedEntityTypeKey]
  );

  const versionHistory = useMemo((): VersionItem[] => {
    if (!versionsData) return [];

    // Find current version's creation time for outdated detection
    const currentVersion = versionsData.find((v) => v.isCurrent);
    const currentVersionTime = currentVersion?._creationTime ?? 0;

    // Count non-draft versions for draft display number
    const nonDraftCount = versionsData.filter((v) => !v.isDraft).length;

    const items: VersionItem[] = versionsData.map((v) => {
      const isDraft = !!v.isDraft;
      const isOutdatedDraft =
        isDraft && currentVersionTime > 0 && v._creationTime < currentVersionTime;

      return {
        id: v._id,
        version: String(v.versionNumber),
        summary: v.summary,
        author: resolveUser(userMap, v.createdBy).fullName,
        timestamp: new Date(v._creationTime),
        isCurrent: v.isCurrent,
        isDraft,
        isOutdatedDraft,
        originKind: v.originKind ?? null,
        originAssistantThreadId: v.originAssistantThreadId ?? null,
        displayVersion: isDraft ? (nonDraftCount + 1).toString() : String(v.versionNumber),
        projectSnapshots: Array.isArray(v.projectSnapshots)
          ? v.projectSnapshots.filter(
              (
                snapshot
              ): snapshot is {
                id: Id<"projectSnapshots">;
                name: string;
                releaseNumber: number;
                kind: "manual" | "ai-baseline" | "ai-checkpoint" | "restored";
              } => snapshot !== null
            )
          : [],
      };
    });

    // Sort by creation time, newest first
    items.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    return items;
  }, [versionsData, userMap]);

  const assistantThreadIds = useMemo(
    () => collectAssistantThreadIds(versionHistory),
    [versionHistory]
  );
  const threadProvenance = useVersionThreadProvenance(projectId, assistantThreadIds);
  const provenanceByThreadId = useMemo(() => {
    const byThreadId = new Map<string, VersionThreadProvenanceView>();
    for (const entry of threadProvenance ?? []) {
      byThreadId.set(entry.threadId, { accessible: entry.accessible, title: entry.title });
    }
    return byThreadId;
  }, [threadProvenance]);

  const resolveThreadProvenance = useCallback(
    (threadId: string | null): VersionThreadProvenanceView | undefined => {
      if (!threadId || threadProvenance === undefined) {
        // Still in flight (or nothing to ask about): the badge says so rather than guessing.
        return undefined;
      }
      // The query answered but this id fell past its scan bound — that is not permission to
      // link, so it reads exactly like a conversation the viewer may not open.
      return provenanceByThreadId.get(threadId) ?? { accessible: false, title: null };
    },
    [provenanceByThreadId, threadProvenance]
  );

  const handleOpenAssistantThread = useCallback(
    (threadId: string) => {
      navigateTab(tabId, buildAssistantThreadPath(threadId), {
        rc: undefined,
      });
    },
    [navigateTab, tabId]
  );

  useEffect(() => {
    if (!versionsData || !entityType || !entityId) return;
    const panelOpenKey = `${projectId}:${entityType}:${entityId}`;
    if (trackedPanelOpenKeyRef.current === panelOpenKey) return;
    trackedPanelOpenKeyRef.current = panelOpenKey;

    trackVersionEvent("version_panel_opened", {
      project_id: projectId,
      entity_type_key: entityType,
      entity_id: entityId,
      version_count: versionHistory.length,
      has_draft: versionHistory.some((version) => version.isDraft),
    });
  }, [entityId, entityType, projectId, versionHistory, versionsData]);

  const hasAnyDraft = versionHistory.some((v) => v.isDraft);
  const currentViewedVersion =
    versionHistory.find((version) =>
      isVersionViewedForRoute({ version, currentViewedVersionId, hasAnyDraft })
    ) ?? null;
  const compareTooltipText = getCompareTooltipText(currentViewedVersion);

  const isVersionViewed = useCallback(
    (version: VersionItem, hasAnyDraft: boolean) =>
      isVersionViewedForRoute({ version, currentViewedVersionId, hasAnyDraft }),
    [currentViewedVersionId]
  );

  const handleVersionSelect = useCallback(
    (version: VersionItem, { hasAnyDraft }: { hasAnyDraft: boolean }) => {
      if (!hasVersionController) {
        return;
      }

      if (getVersionSelectionRouteAction(version, hasAnyDraft) === "base") {
        requestExitVersionPreview();
        return;
      }

      trackTouch("version_preview");
      trackVersionEvent("version_preview_started", {
        project_id: projectId,
        entity_type_key: entityType ?? undefined,
        entity_id: entityId ?? undefined,
        version_id: version.id,
        version_number: getSavedVersionNumber(version),
      });
      requestVersionPreview(version.id, getSavedVersionNumber(version));
    },
    [
      entityId,
      entityType,
      hasVersionController,
      projectId,
      requestExitVersionPreview,
      requestVersionPreview,
      trackTouch,
    ]
  );

  const handleRowKeyDown = useCallback(
    (
      event: KeyboardEvent<HTMLDivElement>,
      rowIndex: number,
      hasAnyDraft: boolean,
      version: VersionItem
    ) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        handleVersionSelect(version, { hasAnyDraft });
        return;
      }

      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const rowButtons =
        event.currentTarget
          .closest("ul")
          ?.querySelectorAll<HTMLDivElement>('li > [data-version-row="true"]') ?? null;
      const step = event.key === "ArrowDown" ? 1 : -1;

      if (isDiffMode && diffTargetVersionId) {
        const viewedIndex = versionHistory.findIndex((candidateVersion) =>
          isVersionViewed(candidateVersion, hasAnyDraft)
        );
        const compareIndex = versionHistory.findIndex(
          (candidateVersion) => candidateVersion.id === diffTargetVersionId
        );
        const pairShift = resolveComparePairShift({
          viewedIndex,
          compareIndex,
          step,
          versionCount: versionHistory.length,
        });

        if (pairShift) {
          const baselineVersion = versionHistory[pairShift.baselineIndex];
          const targetVersion = versionHistory[pairShift.targetIndex];
          if (!baselineVersion || !targetVersion) {
            return;
          }

          trackTouch("version_compare");
          trackVersionEvent("version_compare_started", {
            project_id: projectId,
            entity_type_key: entityType ?? undefined,
            entity_id: entityId ?? undefined,
            baseline_version_id:
              resolveBaselineVersionId(baselineVersion, hasAnyDraft) ?? undefined,
            target_version_id: targetVersion.id,
          });
          requestVersionComparePair(
            resolveBaselineVersionId(baselineVersion, hasAnyDraft),
            targetVersion.id,
            getSavedVersionNumber(baselineVersion),
            getSavedVersionNumber(targetVersion),
            { routeBehavior: getComparePairRouteBehavior(baselineVersion, targetVersion) }
          );
          rowButtons?.item(pairShift.targetIndex)?.focus();
          return;
        }

        const candidateIndex = resolveCompareNavigationTarget({
          versionHistory,
          diffTargetVersionId,
          rowIndex,
          step,
          isVersionViewed: (candidateVersion) =>
            isVersionViewedForRoute({
              version: candidateVersion,
              currentViewedVersionId,
              hasAnyDraft,
            }),
        });

        if (candidateIndex !== null) {
          const candidateVersion = versionHistory[candidateIndex];
          if (!candidateVersion) {
            return;
          }

          trackTouch("version_compare");
          trackVersionEvent("version_compare_started", {
            project_id: projectId,
            entity_type_key: entityType ?? undefined,
            entity_id: entityId ?? undefined,
            baseline_version_id: currentViewedVersionId ?? undefined,
            target_version_id: candidateVersion.id,
          });
          requestVersionCompare(candidateVersion.id, getSavedVersionNumber(candidateVersion), {
            routeBehavior: getCompareRouteBehavior({
              targetVersion: candidateVersion,
              hasAnyDraft,
              currentViewedVersionId,
            }),
          });
          rowButtons?.item(candidateIndex)?.focus();
        }
        return;
      }

      const nextIndex = rowIndex + step;
      if (nextIndex < 0 || nextIndex >= versionHistory.length) {
        return;
      }

      const nextVersion = versionHistory[nextIndex];
      if (!nextVersion) {
        return;
      }

      handleVersionSelect(nextVersion, {
        hasAnyDraft,
      });
      rowButtons?.item(nextIndex)?.focus();
    },
    [
      currentViewedVersionId,
      diffTargetVersionId,
      entityId,
      entityType,
      handleVersionSelect,
      isDiffMode,
      isVersionViewed,
      projectId,
      requestVersionComparePair,
      trackTouch,
      requestVersionCompare,
      versionHistory,
    ]
  );

  // No specific entity selected — show contextual empty state.
  // Only triggers when entityType is explicitly set by a list view (entityId left null).
  // When both are null (e.g., detail view loading data), fall through to loading state.
  if (entityType && !entityId) {
    const typeInfo = getCatalogEntityTypeDisplay(
      frameworkStructure?.compiledCatalog,
      entityDisplays,
      entityType
    );
    const displayName = typeInfo.name;
    const articleLabel = `${typeInfo.article} ${typeInfo.name}`;
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
        <History className="size-10 text-muted-foreground/50" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">No {displayName} selected</p>
          <p className="text-xs text-muted-foreground/70">
            Select {articleLabel} from the sidebar to view its change history
          </p>
        </div>
      </div>
    );
  }

  if (versionsData === undefined) {
    return <LoadingState message="Loading change history..." />;
  }

  if (versionHistory.length === 0) {
    return <EmptyState message="No change history available" />;
  }

  return (
    <ScrollShadow
      className="h-full min-h-0 flex-1"
      colorScheme="raised"
      viewportProps={{
        "data-scroll-affordance-container": "versions-panel",
      }}
    >
      <ul aria-label="Version history" className="list-none">
        {versionHistory.map((version, index) => {
          // Determine which version reflects what's actually in the form:
          // 1. Preview active → the previewed version
          // 2. Draft exists (user is editing) → the draft
          // 3. Otherwise → the current version
          const isViewedVersion = isVersionViewed(version, hasAnyDraft);

          // Determine if clicking this row does something
          const isClickable = hasVersionController;

          // Is this the preview row (non-current version being viewed)?
          const isPreviewRow = isViewedVersion && !version.isCurrent && !version.isDraft;

          // Is this the compare target?
          const isCompareTarget = isDiffMode && diffTargetVersionId === version.id;

          // Show Compare button on non-participating rows
          const showCompareButton =
            !isViewedVersion &&
            !isCompareTarget &&
            hasVersionController &&
            (!version.isCurrent || !!currentViewedVersionId || hasAnyDraft);

          // Determine color state for this row
          const rowState = resolveVersionRowState({
            version,
            isViewedVersion,
            isCompareTarget,
            isPreviewRow,
            isDiffMode,
          });
          const colors = VERSION_ROW_COLORS[rowState];

          // Determine the status label text
          const getLabelText = (): string | null => {
            if (isDiffMode && !version.isDraft && (isViewedVersion || isCompareTarget))
              return "Compare";
            if (rowState === "preview") return "Preview";
            if (rowState === "current") return hasAnyDraft ? "Last saved" : "Current";
            // Draft "Editing" label only when actively viewed
            if (rowState === "draft" && !isViewedVersion) return null;
            return colors.label;
          };

          const labelText = getLabelText();

          return (
            <li key={version.id}>
              {/* biome-ignore lint/a11y/useSemanticElements: This row coordinates selection while containing nested compare and delete buttons. */}
              <div
                role="button"
                tabIndex={0}
                data-version-row="true"
                aria-label={`${version.isDraft ? "Draft" : `Version ${version.displayVersion}`}: ${version.summary}${version.isCurrent ? " (Current)" : ""}${version.isDraft && !version.isOutdatedDraft ? " (Editing)" : ""}${version.isOutdatedDraft ? " (Outdated)" : ""}${isPreviewRow && !isDiffMode ? " (Preview)" : ""}${isCompareTarget ? " (Compare)" : ""}`}
                className={cn(
                  "w-full px-4 py-2.5 text-left transition-colors focus-visible:outline-none",
                  (isViewedVersion || isCompareTarget) && colors.rowBg
                    ? colors.rowBg
                    : "hover:bg-muted/50 focus-visible:bg-muted/50",
                  isClickable ? "cursor-pointer" : "cursor-default"
                )}
                onClick={() =>
                  handleVersionSelect(version, {
                    hasAnyDraft,
                  })
                }
                onKeyDown={(event) => handleRowKeyDown(event, index, hasAnyDraft, version)}
              >
                {/* Line 1: version pill + label + snapshot icon + spacer + date + compare btn */}
                <div className="flex h-7 items-center gap-2.5">
                  {/* Version pill */}
                  <span
                    className={cn(
                      "inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold leading-snug",
                      colors.pillBg,
                      colors.pillText
                    )}
                  >
                    {version.isDraft ? "Draft" : `v${version.displayVersion}`}
                  </span>

                  {/* Draft/outdated icon — pencil only outside compare mode */}
                  {version.isDraft &&
                    !version.isOutdatedDraft &&
                    isViewedVersion &&
                    !isDiffMode && <Pencil className="size-3.5 text-amber-600" />}
                  {version.isOutdatedDraft && <TriangleAlert className="size-3.5 text-red-600" />}

                  {/* Status label */}
                  {labelText && (
                    <span
                      className={cn(
                        "text-xs font-medium",
                        rowState === "current" && !isViewedVersion
                          ? "text-neutral-muted-fg"
                          : colors.labelColor
                      )}
                    >
                      {labelText}
                    </span>
                  )}

                  {/* Snapshot icon */}
                  {version.projectSnapshots.length > 0 && (
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <button
                            type="button"
                            className="inline-flex items-center text-muted-foreground transition-colors hover:text-foreground"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenProjectSnapshots();
                            }}
                            aria-label={`Version ${version.displayVersion} is part of ${version.projectSnapshots.length} project snapshot${version.projectSnapshots.length === 1 ? "" : "s"}`}
                          />
                        }
                      >
                        <Camera className="size-3.5" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-80">
                        <div className="space-y-1 text-xs">
                          <div className="font-medium">Included in project snapshots</div>
                          <ul className="list-disc space-y-1 pl-4">
                            {version.projectSnapshots.map((snapshot) => (
                              <li key={snapshot.id}>{formatSnapshotLabel(snapshot)}</li>
                            ))}
                          </ul>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {/* Spacer — pushes date to the right */}
                  <span className="flex-1" />

                  {/* Date */}
                  <time
                    dateTime={version.timestamp.toISOString()}
                    className={cn("text-[11px] shrink-0 text-neutral-dim")}
                  >
                    {formatSmartDate(version.timestamp.getTime())}
                  </time>

                  {/* Delete draft button — only when draft is actively viewed */}
                  {version.isDraft && isViewedVersion && (
                    <button
                      type="button"
                      className={cn(
                        "inline-flex size-7 items-center justify-center shrink-0 rounded transition-colors hover:bg-muted",
                        version.isOutdatedDraft
                          ? "text-red-600 hover:text-red-700"
                          : "text-amber-600 hover:text-amber-700"
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDraft();
                      }}
                      aria-label="Delete draft"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  )}

                  {/* Compare button (icon-only) */}
                  {showCompareButton && (
                    <Tooltip>
                      <TooltipTrigger
                        delay={0}
                        render={
                          <button
                            type="button"
                            className="inline-flex size-7 items-center justify-center shrink-0 rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rowButton = e.currentTarget
                                .closest("li")
                                ?.querySelector<HTMLDivElement>(
                                  ':scope > [data-version-row="true"]'
                                );
                              rowButton?.focus();
                              trackTouch("version_compare");
                              trackVersionEvent("version_compare_started", {
                                project_id: projectId,
                                entity_type_key: entityType ?? undefined,
                                entity_id: entityId ?? undefined,
                                baseline_version_id: currentViewedVersionId ?? undefined,
                                target_version_id: version.id,
                              });
                              requestVersionCompare(version.id, getSavedVersionNumber(version), {
                                routeBehavior: getCompareRouteBehavior({
                                  targetVersion: version,
                                  hasAnyDraft,
                                  currentViewedVersionId,
                                }),
                              });
                            }}
                            aria-label={`Compare version ${version.displayVersion} with selected version`}
                          />
                        }
                      >
                        <ArrowLeftRight className="size-3.5" />
                      </TooltipTrigger>
                      <TooltipContent side="left">{compareTooltipText}</TooltipContent>
                    </Tooltip>
                  )}
                </div>

                {/* Line 2: author — plus Claudia's mark when the assistant made the change.
                    The badge renders null for every non-AI origin, so a manual save's author
                    line stays exactly the markup it has always been. */}
                <div className="mt-1 text-[11px] font-medium text-neutral-muted-fg">
                  {version.author}
                  <VersionOriginBadge
                    originKind={version.originKind}
                    originAssistantThreadId={version.originAssistantThreadId}
                    authorName={version.author}
                    createdAt={version.timestamp.getTime()}
                    provenance={resolveThreadProvenance(version.originAssistantThreadId)}
                    onOpenChat={handleOpenAssistantThread}
                  />
                </div>

                {/* Line 3: draft status line or change summary */}
                {version.isDraft ? (
                  <p
                    className={cn(
                      "mt-1 text-[10px] font-medium",
                      version.isOutdatedDraft
                        ? "text-red-600"
                        : isViewedVersion
                          ? "text-amber-600"
                          : "text-neutral-dim"
                    )}
                  >
                    {version.isOutdatedDraft
                      ? "Older than the last saved version"
                      : "Your unsaved changes"}
                  </p>
                ) : (
                  <div className={cn("mt-1.5 border-l-2 pl-2", colors.summaryBorder)}>
                    <p className={cn("text-[11px] italic leading-snug", colors.summaryText)}>
                      {getVersionSummaryText(version)}
                    </p>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </ScrollShadow>
  );
}
